/**
 * `/locations/united-states/<stateUrlLabel>/`, for example `/locations/united-states/illinois/`:
 * the locations finder map, opened on that state's `center` and `zoom` from the header location
 * data. It still lists every brand location; only the starting view changes.
 *
 * One page is built per US state in the header location data (Contentful
 * `countryStatesProvinces`), including states with no location. `getStaticPaths` and
 * `getStaticProps` both read that data, the first through `getFlattenedLocationsHeaderData` and
 * the second through `allContentfulHeaderCalls` → `ryersonLocationData`, which calls the first
 * (cached per Node process). So the `find` in `getStaticProps` matches for every generated path;
 * if it didn't, `stateDetails` would be `undefined`, which Next.js refuses to serialise, and the
 * build would fail.
 *
 * No `canonical` is passed to `LocationsOnMapContainer`, so the title is "Find a Ryerson Location
 * Near You - Ryerson" and the canonical is `/locations` on every state page. The Gatsby site this
 * replaced passed `/locations/united-states/<urlLabel>`, and each state page is meant to have its
 * own title and canonical so it is indexed on its own; the prop was lost in the Next.js
 * migration. Passing it back alone isn't enough: the container builds the title from
 * `stateDetails.label`, a per-language `{ en, es, fr }` object here, so it would read
 * "Locations In [object Object] - Ryerson" (see
 * `src/components/Locations/LocationsOnMapContainer.tsx`).
 *
 * No `NEXT_PUBLIC_COMPANY` check, unlike the Canada pages, so the dormant Southern Tool Steel
 * build builds it too. The route tree is in `src/pages/locations/index.tsx`. Pinned by
 * `src/unit-test/pages/locations/united-states/stateUrlLabel/index.spec.tsx`.
 */
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { getAllContentfulLocationsForMap } from "@contentful/location/getAllContentfulLocationsForMap";
import { ProcessedContentfulLocationsByLanguageForMap } from "@contentful/location/types";
import { Location } from "@ryerson/frontend.header-footer";
import {
	getFlattenedLocationsHeaderData,
	allContentfulHeaderCalls,
	HeaderPageProps,
} from "@ryerson/frontend.contentful";

import React from "react";
import { GetStaticProps } from "next";
import { Country } from "@ryerson/frontend.common";
import LocationsOnMapContainer from "@components/Locations/LocationsOnMapContainer";

/** One path per US state `urlLabel` in the header location data. */
export const getStaticPaths = async () => {
	const {
		countries: { [Country.us]: usStates },
	} = await getFlattenedLocationsHeaderData();

	return {
		paths: usStates.map((stateObj) => ({
			params: {
				stateUrlLabel: stateObj.urlLabel,
			},
		})),
		fallback: false, // returns 404 for all other paths
	};
};

type PageParams = {
	stateUrlLabel: string;
};

type PageProps = {
	stateDetails: Location;
	contentfulLocationsForMapByLanguage: ProcessedContentfulLocationsByLanguageForMap;
};

/**
 * Build time: the header data, the state's own header entry as `stateDetails` (its `center` and
 * `zoom` set the map's starting view) and every brand location for the map.
 */
export const getStaticProps: GetStaticProps<PageProps, PageParams> = async ({ params }) => {
	const { props: headerFooterProps } = await allContentfulHeaderCalls();
	return {
		props: {
			...headerFooterProps,
			stateDetails: headerFooterProps.ryersonLocationData.countries[Country.us].find(
				(stateObj) => stateObj.urlLabel === params?.stateUrlLabel
			) as Location, // type narrowing as we know it will be found
			contentfulLocationsForMapByLanguage: await getAllContentfulLocationsForMap(),
		},
	};
};
/** A US state's map page; see the file header. */
const Page: React.FC<HeaderPageProps & PageProps> = (props) => {
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<LocationsOnMapContainer
				stateDetails={props.stateDetails}
				contentfulLocationsForMapByLanguage={props.contentfulLocationsForMapByLanguage}
			/>
		</HeaderLayout>
	);
};

export default Page;
